import React, { useMemo } from 'react'

import {
  NestedDropdown,
  TableComponentV2,
  TextInputV4,
} from '@labrav/react-components'
import { useStyles } from './MarketplaceTemplateDimensions.styles'
import { Typography } from '@mui/material'
import { useLocation } from 'react-router-dom'
import {
  buildSalesforceCrmNestedMenu,
  getInputType,
} from '../../../../utils/mappingManagerConstants'
import {
  MarketplaceFieldMappingDimensions,
  MarketplaceTemplatesDataFieldMappings,
} from '../../../../../../modules/marketplaceMappingManager/types'
import { useDispatch, useSelector } from '../../../../../../store'
import { updateMarketplaceTemplateSectionField } from '../../../../../../modules/marketplaceMappingManager/action'

const customerDetailsMapping = {
  'Product dimensions': 'dimensionName',
  'Salesforce fields': 'salesforceFields',
  'Default value': 'defaultValue',
}

const TABLE_CELL_PROPS = [
  { style: { width: '33%' } },
  { style: { width: '33%' } },
  { style: { width: '33%' } },
]

const EMPTY_SELECTED: string[] = []
const NOOP = () => {}

export function parseCrmField(crmField?: string) {
  if (!crmField || typeof crmField !== 'string') return null
  const parts = crmField.split('.')
  if (parts.length < 2) return null
  return { objectType: parts[0], fieldApi: parts.slice(1).join('.') }
}

function getDimensionDefaultDisplayValue(
  dimension: MarketplaceFieldMappingDimensions
): string {
  const v = dimension.defaultValue
  if (Array.isArray(v)) return String(v[0] ?? '')
  if (v == null) return ''
  return String(v)
}

function dimensionHasMappedCrmField(
  dimension: MarketplaceFieldMappingDimensions
): boolean {
  const v = dimension.crmField
  return typeof v === 'string' && v.trim() !== ''
}

function dimensionHasDefaultValue(
  dimension: MarketplaceFieldMappingDimensions
): boolean {
  const value = dimension.defaultValue
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'boolean') return value
  return value != null && String(value).trim() !== ''
}

function computeDimensionIsMapped(
  dimension: MarketplaceFieldMappingDimensions,
  updates: Partial<MarketplaceFieldMappingDimensions>
): boolean {
  const merged: MarketplaceFieldMappingDimensions = { ...dimension, ...updates }
  return dimensionHasMappedCrmField(merged) || dimensionHasDefaultValue(merged)
}

export const MarketplaceTemplateTableContainer = React.memo(
  ({
    productItem,
    productId,
  }: {
    productItem: {
      dimensions: {
        [key: string]: MarketplaceFieldMappingDimensions
      }
      productName: string
    }
    productId: string
  }) => {
    const { classes } = useStyles()
    const dispatch = useDispatch()
    const location = useLocation()

    const { dimensions } = productItem

    const queryParams = useMemo(
      () => new URLSearchParams(location.search),
      [location.search]
    )
    const isViewOnly = queryParams.get('viewOnly') === 'true'

    const marketplaceTableDetails = useSelector(
      state =>
        state.marketplaceMappingManager?.templateData?.templateData
          ?.fieldMappings?.dimensions?.dimensionsMapping
    )
    const salesforceFieldsData = useSelector(
      state => state.marketplaceMappingManager.salesforceFields.fields || []
    )

    /** Match field-mapping tab: list every Salesforce field for the object, not filtered by referral field type. */
    const crmMenu = useMemo(
      () => buildSalesforceCrmNestedMenu(salesforceFieldsData),
      [salesforceFieldsData]
    )

    const productDimensions = useMemo(() => {
      if (!dimensions) return []
      return Object.entries(dimensions).map(([key, value]) => ({
        key,
        value,
      }))
    }, [dimensions])

    const rowDerivedData = useMemo(() => {
      const menuByKey: Record<string, typeof crmMenu> = {}
      const defaultSelectedByKey: Record<
        string,
        { value: string; path: string }[]
      > = {}

      for (const { key, value: dimensionObj } of productDimensions) {
        menuByKey[key] = crmMenu

        const crmParsed = parseCrmField(dimensionObj?.crmField)
        defaultSelectedByKey[key] =
          crmParsed && dimensionObj?.crmFieldLabel
            ? [
                {
                  value: crmParsed.fieldApi,
                  path: `${crmParsed.objectType}.${dimensionObj?.crmFieldLabel}`,
                },
              ]
            : []
      }

      return { menuByKey, defaultSelectedByKey }
    }, [productDimensions, crmMenu])

    const dimensionRowHandlers = useMemo(() => {
      const map: Record<
        string,
        {
          onDefaultChange: (e: React.ChangeEvent<HTMLInputElement>) => void
          onSalesforceChange: (
            selectedValues: string[],
            selectedPaths: string[]
          ) => void
        }
      > = {}

      for (const { key, value: dimensionObj } of productDimensions) {
        map[key] = {
          onDefaultChange: e => {
            const v = e.target.value
            const updates: Partial<MarketplaceFieldMappingDimensions> = {
              defaultValue: v,
              ...(v ? { crmField: '', crmFieldLabel: '' } : {}),
            }
            dispatch(
              updateMarketplaceTemplateSectionField(
                'dimensions' as keyof MarketplaceTemplatesDataFieldMappings,
                {
                  dimensionsMapping: {
                    ...marketplaceTableDetails,
                    [productId]: {
                      ...productItem,
                      dimensions: {
                        ...dimensions,
                        [key]: {
                          ...dimensionObj,
                          ...updates,
                          isMapped: computeDimensionIsMapped(
                            dimensionObj,
                            updates
                          ),
                        },
                      },
                    },
                  },
                } as any
              )
            )
          },
          onSalesforceChange: (selectedValues, selectedPaths) => {
            const selectedValue = selectedValues[0] || ''
            const selectedPath = selectedPaths[0] || ''
            const pathParts = selectedPath.split('.')
            const crmObject = pathParts[0] || ''
            const crmFieldLabelOnly = selectedValue
              ? pathParts.slice(1).join('.') || selectedPath
              : ''

            const crmFieldVal =
              crmObject && selectedValue
                ? `${crmObject}.${selectedValue}`
                : selectedValue
            const updates: Partial<MarketplaceFieldMappingDimensions> = {
              crmField: crmFieldVal,
              crmFieldLabel: crmFieldLabelOnly,
              ...(selectedValue ? { defaultValue: '' } : {}),
            }

            dispatch(
              updateMarketplaceTemplateSectionField(
                'dimensions' as keyof MarketplaceTemplatesDataFieldMappings,
                {
                  dimensionsMapping: {
                    ...marketplaceTableDetails,
                    [productId]: {
                      ...productItem,
                      dimensions: {
                        ...dimensions,
                        [key]: {
                          ...dimensionObj,
                          ...updates,
                          isMapped: computeDimensionIsMapped(
                            dimensionObj,
                            updates
                          ),
                        },
                      },
                    },
                  },
                } as any
              )
            )
          },
        }
      }
      return map
    }, [
      productDimensions,
      dispatch,
      marketplaceTableDetails,
      productItem,
      dimensions,
      productId,
    ])

    const marketplaceTableData = useMemo(() => {
      return productDimensions.map(({ key, value: dimensionObj }) => {
        const handlers = dimensionRowHandlers[key]
        const menuDataForRow = rowDerivedData.menuByKey[key]
        const defaultSelectedItems = rowDerivedData.defaultSelectedByKey[key]
        const rowHasCrmField = dimensionHasMappedCrmField(dimensionObj)
        const rowHasDefaultValue = dimensionHasDefaultValue(dimensionObj)
        const defaultDisplay = getDimensionDefaultDisplayValue(dimensionObj)

        return {
          id: dimensionObj?.referralField || '',
          dimensionName: (
            <div className={classes.fieldsToMapRow}>
              <Typography className={classes.fieldsToMap}>
                {dimensionObj.label}
              </Typography>
            </div>
          ),
          salesforceFields: (
            <div className={classes.inputSelect}>
              <NestedDropdown
                menuData={menuDataForRow}
                placeholder="Select an option"
                width="381px"
                height="44px"
                allowMultiple={false}
                defaultSelectedItems={defaultSelectedItems}
                onSelectionChange={handlers.onSalesforceChange}
                disabled={isViewOnly || rowHasDefaultValue}
              />
            </div>
          ),
          defaultValue: (
            <div className={classes.inputText}>
              <TextInputV4
                title=""
                placeHolder=""
                updatedValue={defaultDisplay}
                isError={false}
                isReadOnly={isViewOnly || rowHasCrmField}
                isRequired={false}
                errorText=""
                showSpinner={false}
                keyName={`${dimensionObj?.referralField}-defaultValue`}
                type={getInputType(dimensionObj?.referralFieldType || '')}
                value={defaultDisplay}
                onChange={handlers.onDefaultChange}
                keyToPrevent={['+', '-', 'e']}
              />
            </div>
          ),
        }
      })
    }, [
      productDimensions,
      classes,
      rowDerivedData,
      dimensionRowHandlers,
      isViewOnly,
    ])

    const itemHeight = productDimensions.length * 64

    return (
      <div
        data-testid="marketplace-template-form-table"
        className={classes.tableContainer}
      >
        <TableComponentV2
          keyName="marketplace-template-form-table"
          objects={marketplaceTableData}
          mapping={customerDetailsMapping}
          fetchMoreData={NOOP}
          selectedItem={EMPTY_SELECTED}
          onChangeCheckbox={NOOP}
          showCheckbox={false}
          breakWorkHeader={false}
          tableHeight={`calc(100% - ${itemHeight}px)`}
          hasMoreData={false}
          customCellProps={TABLE_CELL_PROPS}
        />
      </div>
    )
  }
)
